import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Determine API base URL based on environment
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Hash password using PBKDF2 with Web Crypto API
 * Uses email as part of the salt for per-user hashing
 */
async function hashPassword(password, email) {
  // Create a salt combining a fixed value with the email
  const saltString = `huhua-shoes-salt-${email}`;
  const encoder = new TextEncoder();
  const saltBuffer = encoder.encode(saltString);

  // Import the password as a key
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );

  // Derive bits using PBKDF2
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 10000,
      hash: 'SHA-256',
    },
    passwordKey,
    256 // 256 bits = 32 bytes
  );

  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(derivedBits));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,
      isHydrating: true,

      setUser: (user) => set({ user }),
      setToken: (token) => {
        set({ token });
      },
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setIsHydrating: (isHydrating) => set({ isHydrating }),

  register: async (email, password, name, phone, gender, country, verificationCode) => {
    set({ isLoading: true, error: null });
    try {
      // Hash password before sending
      const hashedPassword = await hashPassword(password, email);

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password: hashedPassword,
          name,
          phone: phone || null,
          gender: gender || null,
          country: country || null,
          verificationCode: verificationCode || null,
        }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || data.message || 'Registration failed');

      set({ token: data.token, user: data.user, isLoading: false });
      localStorage.setItem('authToken', data.token);
      return data;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      // Hash password before sending
      const hashedPassword = await hashPassword(password, email);

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: hashedPassword }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || data.message || 'Login failed');

      set({ token: data.token, user: data.user, isLoading: false });
      localStorage.setItem('authToken', data.token);
      return data;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('huhua-store');
    set({ user: null, token: null });
  },

  verifyToken: async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok) {
        localStorage.removeItem('authToken');
        set({ user: null, token: null });
        return;
      }

      set({ user: data.user });
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('authToken');
      set({ user: null, token: null });
    }
    finally {
      set({ isHydrating: false });
    }
  },
    }),
    {
      name: 'auth-store',
      partialState: { user: true, token: true },
      onRehydrateStorage: () => {
        return (state) => {
          // onRehydrateStorage callback - optionally update state after rehydration
          state?.setIsHydrating(false);
        };
      },
    }
  )
);
